/*
 * Copyright (c) 2025 Daerik.com
 * This script may not be copied, reproduced or altered in whole or in part.
 * We check the Internet regularly for illegal copies of our scripts.
 * Do not edit or copy this script for someone else, because you will be held responsible as well.
 * This copyright shall be enforced to the full extent permitted by law.
 * Licenses to use this script on a single website may be purchased from Daerik.com
 * @Author: Daerik
 */

import { useLocation } from 'wouter'
import { ChevronLeft, ChevronRight, Bell, AlertCircle, Gift, Cloud, DollarSign, Wrench } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { cn } from '@/lib/utils'

// Placeholder notification type - will be replaced when backend is ready
interface Notification {
	id: string
	type: 'bell' | 'alert' | 'gift' | 'cloud' | 'money' | 'tools'
	title: string
	timestamp: string
	isNew?: boolean
	isUrgent?: boolean
}

// Empty placeholder - notifications will come from backend later
const notifications: Notification[] = []

// Group notifications by time period
const groupNotifications = (notifications: Notification[]) => {
	const now = new Date()
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

	const groups: { label: string; items: Notification[] }[] = []
	const newItems: Notification[] = []
	const todayItems: Notification[] = []
	const yesterdayItems: Notification[] = []
	const olderItems: Notification[] = []

	notifications.forEach((notification) => {
		if(notification.isNew) {
			newItems.push(notification)
		} else {
			// For now, just distribute based on some logic
			// This will be replaced with actual timestamp parsing
			todayItems.push(notification)
		}
	})

	if(newItems.length > 0) groups.push({ label: 'New', items: newItems })
	if(todayItems.length > 0) groups.push({ label: 'Today', items: todayItems })
	if(yesterdayItems.length > 0) groups.push({ label: 'Yesterday', items: yesterdayItems })
	if(olderItems.length > 0) groups.push({ label: 'Earlier', items: olderItems })

	return groups
}

const getNotificationIcon = (type: Notification['type'], isUrgent?: boolean) => {
	const iconClass = 'h-5 w-5'

	switch(type) {
		case 'bell':
			return (
				<div className={cn(
					'h-10 w-10 rounded-full flex items-center justify-center',
					isUrgent ? 'bg-yellow-500/20' : 'bg-yellow-500/20'
				)}>
					<Bell className={cn(iconClass, 'text-yellow-500')}/>
				</div>
			)
		case 'alert':
			return (
				<div className="h-10 w-10 rounded-full bg-orange-500/20 flex items-center justify-center">
					<AlertCircle className={cn(iconClass, 'text-orange-500')}/>
				</div>
			)
		case 'gift':
			return (
				<div className="h-10 w-10 rounded-full bg-amber-600/20 flex items-center justify-center">
					<Gift className={cn(iconClass, 'text-amber-600')}/>
				</div>
			)
		case 'cloud':
			return (
				<div className="h-10 w-10 rounded-full bg-blue-500/20 flex items-center justify-center">
					<Cloud className={cn(iconClass, 'text-blue-500')}/>
				</div>
			)
		case 'money':
			return (
				<div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
					<DollarSign className={cn(iconClass, 'text-green-500')}/>
				</div>
			)
		case 'tools':
			return (
				<div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center">
					<Wrench className={cn(iconClass, 'text-red-500')}/>
				</div>
			)
		default:
			return (
				<div className="h-10 w-10 rounded-full bg-[#3C3C3C] flex items-center justify-center">
					<Bell className={cn(iconClass, 'text-[#BABABB]')}/>
				</div>
			)
	}
}

const NotificationItem = ({ notification }: { notification: Notification }) => {
	return (
		<div className="flex items-center gap-3 p-4 bg-[#3C3C3C] rounded-xl hover:bg-[#4C4C4C] transition-colors cursor-pointer">
			<div className="flex items-center gap-3 flex-1">
				{notification.isNew && (
					<div className="w-2 h-2 rounded-full bg-white shrink-0"/>
				)}
				{getNotificationIcon(notification.type, notification.isUrgent)}
				<div className="flex-1 min-w-0">
					<p className="text-[#ffffff] text-sm leading-snug line-clamp-2">
						{notification.title}
					</p>
					<p className="text-[#BABABB] text-xs mt-1">
						{notification.timestamp}
					</p>
				</div>
			</div>
			<ChevronRight className="h-5 w-5 text-[#BABABB] shrink-0"/>
		</div>
	)
}

const EmptyState = () => {
	return (
		<div className="flex flex-col items-center justify-center flex-1 px-6 py-20">
			{/* Mailbox Icon */}
			<div className="relative mb-8">
				{/* Mailbox body */}
				<div className="w-24 h-20 bg-gradient-to-b from-blue-400 to-blue-600 rounded-2xl relative">
					{/* Flag */}
					<div className="absolute -top-3 -right-2 w-3 h-8">
						<div className="w-3 h-3 bg-red-500 rounded-sm"/>
						<div className="w-0.5 h-5 bg-[#3C3C3C] mx-auto"/>
					</div>
					{/* Checkmark circle */}
					<div className="absolute -bottom-2 -left-2 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center border-4 border-[#18181B]">
						<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
						</svg>
					</div>
					{/* Mail slot */}
					<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-1.5 bg-blue-700 rounded-full"/>
				</div>
				{/* Post */}
				<div className="w-4 h-6 bg-gradient-to-b from-blue-500 to-blue-700 mx-auto rounded-b-lg"/>
			</div>

			<h2 className="text-[#ffffff] text-xl font-semibold mb-2">
				No Notifications Yet
			</h2>
			<p className="text-[#BABABB] text-sm text-center max-w-xs">
				Your notifications will appear here once you've received them.
			</p>
		</div>
	)
}

export default function NotificationsPage() {
	const [, navigate] = useLocation()
	const groupedNotifications = groupNotifications(notifications)
	const hasNotifications = notifications.length > 0

	return (
		<ErrorBoundary>
			<div className="min-h-screen bg-[#18181B] flex flex-col">
				<div className="max-w-5xl mx-auto w-full px-5 pb-8">
					{/* Header with back button */}
					<div
						role="banner"
						className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
					>
						<button
							type="button"
							aria-label="Go back"
							onClick={() => navigate('/')}
							className="absolute left-0
								h-8 w-8
								flex items-center justify-center
								rounded-full
								bg-white/10 hover:bg-white/20
								focus:outline-none focus:ring-2 focus:ring-white/40"
						>
							<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]'/>
						</button>

						<h1 className="text-base font-semibold tracking-wide">
							Notifications
						</h1>
					</div>
				</div>

				{/* Content */}
				{hasNotifications ? (
					<div className="flex-1 overflow-y-auto max-w-5xl mx-auto w-full px-5 pb-8">
						{groupedNotifications.map((group) => (
							<div key={group.label} className="mb-6">
								<h3 className="text-[#ffffff] text-sm font-medium mb-3 px-1">
									{group.label}
								</h3>
								<div className="space-y-2">
									{group.items.map((notification) => (
										<NotificationItem
											key={notification.id}
											notification={notification}
										/>
									))}
								</div>
							</div>
						))}
					</div>
				) : (
					<EmptyState/>
				)}
			</div>
		</ErrorBoundary>
	)
}